import { useCallback, useEffect, useRef, useState } from "react";
import { useHeadingRef } from "@/lib/barTitle";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Play, Shuffle, Heart, MoreHorizontal, Loader2, Plus, Trash2, Download, ImagePlus, Disc3, Sparkles, BookOpen } from "lucide-react";
import { EmptyState } from "@/components/PageHeader";
import { startDownload } from "@/pages/Playlists";
import { useOwnerView } from "@/components/LibrarySwitch";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Cover } from "@/components/Cover";
import { TrackTable, AddToPlaylistItems } from "@/components/TrackTable";
import { REMOVE_NOTE } from "@/components/SelectionBar";
import { usePlayer } from "@/context/PlayerContext";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { useCrumb } from "@/lib/trail";
import { useBuildMix } from "@/lib/mix";
import { rgb, useArtTint } from "@/lib/artColor";
import { usePageWash } from "@/lib/pageTint";

const ALBUMS_PARENT = { label: "Albums", path: "/albums?sort=name" };
import { MoveProgress, MoveOutButton, useOnMoveDone } from "@/components/MoveToOneDrive";
import { CopyProgress, CopyToLibraryButton } from "@/components/CopyToLibrary";
import { CoverFinder } from "@/components/CoverFinder";
import { PinButton } from "@/components/PinButton";
import { findPin } from "@/lib/offline";

export default function AlbumDetail() {
  const { viewing, canDownload } = useOwnerView();
  const { id } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [finder, setFinder] = useState(false);
  useCrumb(album?.name, ALBUMS_PARENT);
  const p = usePlayer();
  const buildMix = useBuildMix();
  const tint = useArtTint(album?.has_cover ? album.id : null);
  usePageWash(album?.has_cover ? rgb(tint) : null);
  useEffect(() => {
    setAlbum(null);
    api.get(`/albums/${id}`).then((r) => setAlbum(r.data)).catch(() => {
      const p = findPin("album", id);
      setAlbum(p ? { id, name: p.name, artist: p.subtitle, tracks: p.tracks, has_cover: true, source: "offline", offline: true } : false);
    });
  }, [id]);
  useOnMoveDone(useCallback((s) => {
    const next = s.album_map?.[id];
    if (next) navigate(`/album/${next}`, { replace: true });
  }, [id, navigate]));
  const songCount = album ? album.tracks.length : 0;
  const starRef = useRef(() => {});
  const headingRef = useHeadingRef(album ? album.name : "", album ? `${album.artist} · ${songCount} ${songCount === 1 ? "song" : "songs"}` : "",
    album ? { play: () => p.playTracks(album.tracks), shuffle: () => p.playTracks([...album.tracks].sort(() => Math.random() - 0.5)), disabled: !songCount,
      starred: !!album.starred, onStar: viewing ? undefined : () => starRef.current() } : null);
  if (album === false) return <EmptyState icon={Disc3} title="Album not found" text="It may have been removed from the library." />;
  if (!album) return <Loader2 className="animate-spin text-zinc-400" />;

  const total = album.tracks.reduce((s, t) => s + (t.duration || 0), 0);
  const star = async () => { const { data } = await api.post(`/albums/${id}/star`); setAlbum({ ...album, starred: data.starred }); };
  starRef.current = star;
  const shuffled = () => [...album.tracks].sort(() => Math.random() - 0.5);
  const markAudiobook = async () => {
    try { await api.patch(`/audiobooks/${album.id}`, { is_audiobook: true }); toast.success("Moved to Audiobooks"); navigate(`/audiobooks/${album.id}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAlbum = async () => {
    if (!window.confirm(`Remove "${album.name}" and its ${album.tracks.length} songs from your library?\n\n${REMOVE_NOTE}`)) return;
    try {
      await api.post("/library/remove", { album_ids: [album.id] });
      toast.success(`Removed ${album.name}`);
      navigate("/");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="animate-rise">
      <div className="flex flex-col gap-8 md:flex-row md:items-start" data-testid="album-hero">
        <div className="relative w-full max-w-xs shrink-0 md:w-60">
          <Cover albumId={album.id} hasCover={album.has_cover} version={album.cover_version} alt={album.name} className="aspect-square w-full shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]" iconSize={64} />
          {!viewing && (
            <button type="button" onClick={() => setFinder(true)} data-testid="album-change-cover"
              className={`absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow transition-colors ${album.has_cover ? "bg-white/90 text-zinc-800 hover:bg-white" : "bg-zinc-900 text-white hover:bg-zinc-700"}`}>
              <ImagePlus size={12} />{album.has_cover ? "Change cover" : "Add cover"}
            </button>
          )}
          <CoverFinder album={album} open={finder} onOpenChange={setFinder} onApplied={(d) => setAlbum((a) => ({ ...a, has_cover: true, cover_version: d.cover_version }))} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">
            Album · {{ plex: "Plex", onedrive: "OneDrive", webdav: "FileJump", upload: "Uploaded", offline: "Saved on this device" }[album.source]}
          </p>
          <h1 ref={headingRef} className="mt-2 font-heading text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl" data-testid="album-title">{album.name}</h1>
          <p className="mt-2 text-sm text-zinc-600" data-testid="album-counts">
            <Link to={`/artist?name=${encodeURIComponent(album.artist)}`} className="font-medium text-zinc-900 hover:underline" data-testid="album-artist-link">{album.artist}</Link>
            <span className="font-mono text-zinc-500">
              {album.year ? ` · ${album.year}` : ""}{album.genre ? ` · ${album.genre}` : ""} · {album.tracks.length} {album.tracks.length === 1 ? "song" : "songs"} · {fmtTime(total)}
            </span>
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={() => p.playTracks(album.tracks)} data-testid="album-play-button"
              className="flex h-12 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm font-medium text-white transition-transform hover:scale-[1.03] active:scale-95">
              <Play size={16} fill="currentColor" /> Play
            </button>
            <button onClick={() => p.playTracks(shuffled())} data-testid="album-shuffle-button"
              className="flex h-12 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm transition-colors hover:border-zinc-900">
              <Shuffle size={15} /> Shuffle
            </button>
            <button onClick={() => buildMix(album.tracks, { artists: album.artist && !/various/i.test(album.artist) ? [album.artist] : [] })} data-testid="album-build-mix-button" title={`Build a new playlist around “${album.name}”`}
              className="flex h-12 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-5 text-sm text-amber-900 transition-colors hover:border-amber-600">
              <Sparkles size={15} /> Build a mix
            </button>
            {!viewing && <button onClick={star} data-testid="album-star-button" className={`flex h-12 w-12 items-center justify-center rounded-full border border-zinc-300 bg-white transition-colors hover:border-zinc-900 ${album.starred ? "text-amber-600" : "text-zinc-600"}`}>
              <Heart size={17} fill={album.starred ? "currentColor" : "none"} />
            </button>}
            {viewing ? (canDownload && (<>
              <button onClick={() => startDownload(`/albums/${album.id}/download?lib=owner`, "Album download started")} data-testid="album-download-zip"
                className="flex h-12 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900"><Download size={15} />Download</button>
              <CopyToLibraryButton body={{ album_ids: [album.id] }} testid="album-copy-to-library" title={`Save a copy of “${album.name}” into your own library`} />
            </>)) : <DropdownMenu>
              <DropdownMenuTrigger data-testid="album-more-menu" className="flex h-12 w-12 items-center justify-center rounded-full text-zinc-500 hover:text-zinc-900"><MoreHorizontal size={18} /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem onClick={() => p.addToQueue(album.tracks)} data-testid="album-add-queue"><Plus size={14} className="mr-2" />Add to queue</DropdownMenuItem>
                <AddToPlaylistItems trackIds={album.tracks.map((t) => t.id)} />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => startDownload(`/albums/${album.id}/download`, "Album download started")} data-testid="album-download-zip"><Download size={14} className="mr-2" />Download album (ZIP)</DropdownMenuItem>
                <DropdownMenuItem onClick={markAudiobook} data-testid="album-mark-audiobook"><BookOpen size={14} className="mr-2" />Mark as audiobook</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={removeAlbum} className="text-red-600" data-testid="album-remove-library"><Trash2 size={14} className="mr-2" />Remove from library</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>}
            {(!viewing || canDownload) && <PinButton type="album" id={album.id} name={album.name} subtitle={album.artist} tracks={album.tracks} coverAlbumIds={[album.id]} lib={viewing ? "owner" : undefined} testid="album-pin-offline" />}
            {!viewing && album.source === "upload" && <MoveOutButton albumIds={[album.id]} testidPrefix="album-move" />}
          </div>
          {!viewing && album.source === "upload" && <MoveProgress />}
          {viewing && canDownload && <CopyProgress />}
        </div>
      </div>
      <div className="mt-14">
        <TrackTable tracks={album.tracks} showAlbum={false} selectable={!viewing} readOnly={viewing} onTracksChange={(tracks) => setAlbum((a) => ({ ...a, tracks }))} />
      </div>
    </div>
  );
}
